import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { recurringPaymentsApi } from '../../../services/recurringPaymentsApi';
import { GeoPricingConfig } from '../../../types/payments';

interface GeoPricingContextProps {
    geoConfig: GeoPricingConfig | null;
    isLoading: boolean;
    error: string | null;
    refreshPricing: () => Promise<void>;
    countryOverride: string | null;
}

const GeoPricingContext = createContext<GeoPricingContextProps | undefined>(undefined);

export const GeoPricingProvider = ({ children }: { children: ReactNode }) => {
    const [geoConfig, setGeoConfig] = useState<GeoPricingConfig | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Permitir override por URL para QA (ej: ?country=MX)
    const urlParams = new URLSearchParams(window.location.search);
    const override = urlParams.get('country');
    const [countryOverride] = useState<string | null>(override);

    const fetchPricing = async (force = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await recurringPaymentsApi.getGeoPricing(
                countryOverride || undefined,
                { force }
            );
            setGeoConfig(data);
        } catch (err: any) {
            console.error('[GeoPricing] Error fetching pricing:', err);
            setError(err.message || 'Error cargando precios');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPricing();
    }, []);

    return (
        <GeoPricingContext.Provider
            value={{
                geoConfig,
                isLoading,
                error,
                refreshPricing: () => fetchPricing(true),
                countryOverride,
            }}
        >
            {children}
        </GeoPricingContext.Provider>
    );
};

export const useGeoPricingContext = () => {
    const context = useContext(GeoPricingContext);
    if (context === undefined) {
        throw new Error('useGeoPricingContext must be used within a GeoPricingProvider');
    }
    return context;
};
